import React from 'react';

import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import SelectedMembersOptions from 'components/teams/SelectedMembersOptions';

import { personDisplayName } from 'components/helpers/users';

export default function SelectedMembersList({
  domainTeamMembers,
  onChange,
  onTeamMemberDelete,
  team,
}) {
  const queryClient = useQueryClient();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'teams.SelectedMembersList',
  });

  const { t: i18nTTeams } = useTranslation('common', {
    keyPrefix: 'teams',
  });

  const collectedAssignableMembers = queryClient
    .getQueriesData(['assignableMembers'])
    .map((queryResult) => {
      const [_queryKey, queryData] = queryResult;
      if (!queryData) return null;
      return queryData.pages.map((pageData) => pageData.data).flat();
    })
    .flat()
    .filter(Boolean);

  const currentTeamMembers = team.members
    .map((member) => {
      const selectedMember = (
        member.persisted ?
          domainTeamMembers.collection
        : collectedAssignableMembers).find(
        (assignableMember) => assignableMember.id === member.id,
      );
      if (!selectedMember) return null;
      const isStatusChangeEnabled = !!(member.userId && member.personnelId);

      let tooltip;
      if (!isStatusChangeEnabled) {
        const name = selectedMember.attributes.firstName;

        tooltip =
          selectedMember.relationships.user.data === null ?
            i18nT('tooltip.noUser', {
              interpolation: { escapeValue: false },
              name,
            })
          : i18nTTeams('cantBeMadeMember', {
              interpolation: { escapeValue: false },
              name,
            });
      }

      return {
        ...member,
        isStatusChangeEnabled,
        name: personDisplayName({ attributes: selectedMember.attributes }),
        tooltip,
      };
    })
    .filter(Boolean);

  return (
    <div className='form-container'>
      {currentTeamMembers.map((member) => (
        <div
          className='tw-group/pill chip-item-pill tw-relative tw-mb-2 tw-flex tw-h-10 tw-w-full tw-items-center tw-justify-between tw-rounded-md tw-bg-grey-050 tw-px-3 tw-py-1.5'
          key={member.id}
        >
          <div className='tw-w-52 tw-text-s tw-font-medium tw-tracking-wide'>
            {member.name}
          </div>
          <div className='tw-flex tw-items-center'>
            <SelectedMembersOptions member={member} onChange={onChange} />
            <span
              className="collection-select__multivalue-remove circle--remove tw-ml-2 tw-bg-transparent before:tw-bg-grey-700 before:tw-content-[''] after:tw-bg-grey-700 after:tw-content-[''] hover:tw-bg-red-600 hover:before:tw-bg-white hover:after:tw-bg-white"
              onClick={() => onTeamMemberDelete(member.id)}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

SelectedMembersList.propTypes = {
  domainTeamMembers: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onTeamMemberDelete: PropTypes.func.isRequired,
};
